import { Mic, Calendar } from '../../icons';
import { formatDate } from '../../lib/base-i18n';
import { tMinutes } from '../../lib/minutes-i18n';
import type { VoiceSessionItem } from '../../api/voice-sessions';
import { sessionSortAt } from './awaiting-summary';

/**
 * 회의록이 아직 없는 녹음 «한 줄»(목록 보기) · «한 카드»(카드 보기).
 *
 * ## ⭐⭐ 2026-10-20 (④ · 사용자 결정) — 회의록 줄·카드와 «같은 모양» · 「대기」 · 단추 없음
 *
 * 사용자 원문: 「웹에서만 전체,정리대기 탭에서 보면 상단에 정리대기로 표시된다. 다른 목록과 동일하게 디자인하고
 * 대기로 표시해달라.」 ⇒ 시안 ④ 의 두 갈래 중 ★ «후 A — 대기 줄에 단추 없음»으로 정했다(같은 날 B 를 먼저 골랐다가
 * 바꿨다 — 「①②③ 시안대로 진행해줘 ④ 웹 후 A · (고르지 않음) 대기 줄에 단추 없음 으로 진행」).
 * ```
 * 같은 칸     아이콘 칸 · 제목 · 날짜 · 만든 날 · 상태 배지(회의록의 「대기」 — minutes.status.pending)
 * 같은 꼴     minutes-row / minutes-meetcard ＋ 줄 띠 minutes-item--st-pending
 * 걷은 것     노란 테두리 · 조각 수 · 「전사 실패 n」 칩 · 「정리 대기」 배지 · 「요약하기」 단추 ·
 *            엔진 꺼짐 띠 · 누른 뒤의 말(MinutesSummarizeSay)
 * ```
 * ⇒ 요약은 ★ 회의록 상세의 「요약」 탭에서만 건다(`MinutesSummaryPanel` — 같은 단추 · 같은 판정 · 같은 말).
 * ⚠ 대가(사용자에게 알렸다): 회의록이 «없는» 녹음(지운 회의록 · 아직 정리 중)은 웹에서 요약을 못 건다 — 앱은 된다.
 * ⚠ 「참석 n명」은 못 그린다 — 음성 세션 목록 응답에 참석자가 «없다»(`VoiceSessionItem`).
 *
 * ## ⛔ 줄 «자체»는 단추가 아니다 — 열 상세가 없다
 *
 * 회의록 줄은 누르면 상세가 열린다(회의록 id 가 있다). 이 줄은 `minutes_id` 가 null 이다(그것이 이 줄의 «정의»다)
 * ⇒ 같은 `<button>` 으로 그리면 「눌렀는데 아무것도 안 열린다」가 된다 ⇒ `div` 로 그리고 hover 도 걷는다(CSS).
 *
 * ## 종전(2026-10-04 F1-c ~ 10-07) — 지운 갈래의 까닭은 git 이력에 있다
 *
 * 「요약하기」를 이 줄에 두고 ㉠(글자 0 이면 안 부르고 말한다 · 10-05) · ⓑ(실패 조각이 있으면 안 부르고 말한다 ·
 * 10-07)를 여기서 했다. 그 판정(`hasNothingToSummarize` · `summaryWouldStall`)과 말(`MinutesSummarizeSay`)은
 * 회의록 상세가 그대로 쓴다.
 */
export function AwaitingSummaryRow({
  s,
  index = 0,
  mode = 'list',
}: {
  s: VoiceSessionItem;
  index?: number;
  /** ⭐ 2026-10-20 (④) — 목록 보기는 줄, 카드 보기는 카드(회의록과 같은 격자에 섞인다). */
  mode?: 'list' | 'card';
}): JSX.Element {
  const at = sessionSortAt(s);
  if (mode === 'card') {
    return (
      <div
        className="minutes-meetcard minutes-meetcard--anim minutes-meetcard--voice minutes-item--st-pending"
        style={{ animationDelay: `${Math.min(index, 10) * 60}ms` }}
        data-session-id={s.session_id}
      >
        <div className="minutes-card-top">
          <span className="minutes-card-title">{s.title}</span>
          <span className="minutes-status minutes-status--pending">{tMinutes('minutes.status.pending')}</span>
        </div>
        <div className="minutes-card-meta">
          <span className="minutes-chip"><Calendar size={12} aria-hidden />{formatDate(at)}</span>
        </div>
        <div className="minutes-card-created">{tMinutes('minutes.list.created', { date: formatDate(s.created_at) })}</div>
      </div>
    );
  }
  return (
    <div
      className="minutes-row minutes-row--anim minutes-row--voice minutes-item--st-pending"
      style={{ animationDelay: `${Math.min(index, 10) * 50}ms` }}
      data-session-id={s.session_id}
    >
      <span className="minutes-row-ico" aria-hidden><Mic size={15} /></span>
      <span className="minutes-row-title">{s.title}</span>
      <span className="minutes-row-meta">
        <span className="minutes-chip"><Calendar size={12} aria-hidden />{formatDate(at)}</span>
      </span>
      <span className="minutes-row-created">{formatDate(s.created_at)}</span>
      <span className="minutes-status minutes-status--pending">{tMinutes('minutes.status.pending')}</span>
    </div>
  );
}
